<template>
  <div v-if="visible" class="compat-boot-panel">
    <div class="compat-boot-head">
      <strong>兼容诊断 L{{ level }}</strong>
      <span class="compat-boot-name">{{ levelInfo?.name }}</span>
      <button type="button" class="compat-boot-close" aria-label="关闭" @click="dismiss">×</button>
    </div>
    <p class="compat-boot-desc">{{ levelInfo?.desc }}</p>
    <div class="compat-boot-row">
      <span class="compat-boot-label">Vue</span>
      <span :class="boot.vue ? 'ok' : 'fail'">{{ boot.vue ? '已挂载' : '未挂载' }}</span>
    </div>
    <div v-if="boot.error" class="compat-boot-error">{{ boot.error }}</div>
    <div class="compat-boot-row compat-boot-ua">{{ boot.ua }}</div>
    <ul class="compat-boot-features">
      <li v-for="item in levelInfo?.enabled" :key="item">{{ item }}</li>
    </ul>
    <div class="compat-boot-actions">
      <a v-if="level > 0" :href="prevHref" class="compat-btn">← L{{ level - 1 }}</a>
      <a v-if="level < COMPAT_MAX_LEVEL" :href="nextHref" class="compat-btn compat-btn-primary">
        L{{ level + 1 }} →
      </a>
      <a :href="fullHref" class="compat-btn">完整版</a>
    </div>
    <a class="compat-hub-link" :href="hubHref">打开诊断入口页</a>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import {
  COMPAT_LEVELS,
  COMPAT_MAX_LEVEL,
  compatLevelUrl,
  clearCompatLevel,
  type CompatLevelInfo,
} from '@/utils/compatMode'

const props = defineProps<{
  level: number
}>()

const visible = ref(true)
const boot = reactive({
  vue: false,
  ua: '',
  error: '',
})

const levelInfo = computed<CompatLevelInfo | undefined>(() =>
  COMPAT_LEVELS.find((l) => l.level === props.level),
)

const localePrefix = computed(() =>
  typeof window !== 'undefined' && window.location.pathname.includes('/en-US') ? '/en-US' : '/zh-CN',
)

const homePath = computed(() => `${localePrefix.value}`)
const hubHref = computed(() => `${localePrefix.value}/compat`)
const prevHref = computed(() => compatLevelUrl(Math.max(0, props.level - 1), homePath.value))
const nextHref = computed(() => compatLevelUrl(Math.min(COMPAT_MAX_LEVEL, props.level + 1), homePath.value))
const fullHref = computed(() => {
  clearCompatLevel()
  const path = typeof window !== 'undefined' ? window.location.pathname : homePath.value
  const url = new URL(path, 'http://local')
  url.searchParams.delete('compat')
  return `${url.pathname}${url.search}`
})

const dismiss = () => {
  visible.value = false
}

onMounted(() => {
  boot.vue = true
  boot.ua = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : ''
  const err = (window as Window & { __MOJI_COMPAT_ERROR__?: string }).__MOJI_COMPAT_ERROR__
  if (err) boot.error = err
})
</script>

<style scoped>
.compat-boot-panel {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: 12px;
  max-width: 420px;
  margin: 0 auto;
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.96);
  color: #f8fafc;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  z-index: 100000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.compat-boot-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.compat-boot-name {
  flex: 1;
  opacity: 0.85;
  font-size: 12px;
}

.compat-boot-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.compat-boot-desc {
  margin: 0 0 10px;
  opacity: 0.75;
  font-size: 12px;
}

.compat-boot-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.compat-boot-label {
  opacity: 0.65;
}

.ok {
  color: #4ade80;
}

.fail {
  color: #f87171;
}

.compat-boot-error {
  margin: 8px 0;
  padding: 8px;
  background: rgba(248, 113, 113, 0.15);
  border-radius: 6px;
  color: #fecaca;
  font-size: 11px;
  word-break: break-all;
}

.compat-boot-ua {
  font-size: 10px;
  opacity: 0.55;
  word-break: break-all;
  display: block;
}

.compat-boot-features {
  margin: 8px 0 12px;
  padding-left: 18px;
  font-size: 11px;
  opacity: 0.8;
}

.compat-boot-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.compat-btn {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  font-size: 12px;
}

.compat-btn-primary {
  background: #2563eb;
}

.compat-hub-link {
  display: block;
  margin-top: 10px;
  font-size: 11px;
  color: #93c5fd;
}
</style>
